import React, { ChangeEvent, useState, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { debounce } from 'lodash'

import OpenLinkIcon from '../icons/arrow-up-right-from-square.svg?react'
import SearchIcon from '../icons/magnifying-glass.svg?react'
import { getCsrfToken } from '../utils/api_helper'
import './global_search.scss'

const categoryMap: { [key: string]: string } = {
  applicants: 'Applicants',
  applications: 'Applications',
}

/**
 * Function to render a list group for an object type, e.g. applicants, applications.
 */
function ResultListGroup(
  category: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[],
  url: string,
): React.ReactElement {
  const label = categoryMap[category]

  if (!label) {
    console.info('Unsupported results.')
    return <></>
  }

  const renderMoreResults = (url: string) => {
    return (
      <div className="row text-end mt-2">
        <span>
          <a
            href={url || 'javascript: void(0);'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {url && 'Show more results'}
          </a>
        </span>
      </div>
    )
  }

  // Render text for different categories
  if (category === 'applicants') {
    return (
      <div className="list-group list-group-flush text-start p-0 mb-3">
        <label className="form-label">{label}</label>
        {items &&
          items.length > 0 &&
          items.map((item) => (
            <a
              key={item.html_url}
              href={item.html_url}
              className="list-group-item list-group-item-action"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={item.flag_url}
                width="24"
                alt={item.citizenship}
                title={item.citizenship}
              />
              <span className="ps-2 global-search-result-item">
                <span>
                  <b>
                    {item.first_name} {item.last_name}{' '}
                  </b>
                </span>
                <span className="ps-1">{`<${item.email}>`} </span>
                <span className="text-muted ps-1">
                  {item.date_of_birth}
                  <OpenLinkIcon
                    className="fa svg-inline--fa ms-1 text-primary font-14"
                    fill="currentColor"
                  />
                </span>
              </span>
            </a>
          ))}
        {renderMoreResults(url)}
      </div>
    )
  }
  if (category === 'applications') {
    return (
      <div className="list-group list-group-flush text-start p-0 mb-3">
        <label className="form-label">{label}</label>
        {items &&
          items.length > 0 &&
          items.map((item) => (
            <a
              key={item.html_url}
              href={item.html_url}
              className="list-group-item list-group-item-action"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="d-flex">
                <img
                  src={item.applicant?.flag_url}
                  width="24"
                  alt={item.applicant?.citizenship}
                  title={item.applicant?.citizenship}
                />
                <div className="d-flex flex-column">
                  <span className="ps-2 global-search-result-item">
                    <span>
                      <b>
                        {item.applicant?.first_name}{' '}
                        {item.applicant?.last_name}{' '}
                      </b>
                    </span>
                    <span className="ps-1">
                      {`<${item.applicant?.email}>`}{' '}
                    </span>
                    <span className="text-muted ps-1">
                      {item.applicant?.date_of_birth}{' '}
                    </span>
                  </span>
                  <span className="ps-2 fs-6 global-search-result-item">
                    <span>
                      <i>{item.course?.name} </i>
                    </span>
                    <span className="text-muted fw-light ps-1">
                      {item.course?.institution?.name}
                      <OpenLinkIcon
                        className="fa svg-inline--fa ms-1 text-primary font-14"
                        fill="currentColor"
                      />
                    </span>
                  </span>
                </div>
              </div>
            </a>
          ))}
        {renderMoreResults(url)}
      </div>
    )
  }
  return <></>
}

/**
 * Function to render the search list view
 */
function GlobalSearch(): React.ReactElement {
  const placeholder = 'Search applicants or applications...'
  const defaultHelpText =
    'Search for applicants or applications linked to your branch.'
  const countLimit = 5

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [resultsList, setResultsList] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [helpText, setHelpText] = useState<string>(defaultHelpText)

  const keywordInputElement = useRef<HTMLInputElement>(null)

  const resetInput = () => {
    setResultsList([])
    setIsLoading(false)
    setHelpText(defaultHelpText)
    const ele = keywordInputElement.current
    if (ele) {
      ele.value = ''
    }
  }

  // Mobile
  const modalElement = document.querySelector(
    '#global-search-modal',
  ) as HTMLElement
  modalElement?.addEventListener('hide.bs.modal', resetInput)

  // Desktop
  const dropdownElement = document.querySelector(
    '.global-search-dropdown',
  ) as HTMLElement
  dropdownElement?.addEventListener('show.bs.dropdown', resetInput)

  const mobileContainer = document.querySelector('.hyper-global-search-mobile')
  const isMobile =
    getComputedStyle(mobileContainer as HTMLElement)?.display === 'block'

  const fetchResults = async (value: string) => {
    if (!value || value.length < 3) return
    setIsLoading(true)
    try {
      const resp = await fetch(
        `/partner/search/?q=${encodeURIComponent(value)}&limit=${countLimit}`,
        {
          method: 'GET',
          headers: {
            'x-csrftoken': getCsrfToken(),
          },
        },
      )
      if (!resp.ok) {
        setResultsList([])
        setHelpText('No results found.')
        setIsLoading(false)
        return
      }
      const data = await resp.json()
      setResultsList([...(data.results ?? [])])
      setHelpText('No results found.')
      setIsLoading(false)
    } catch (e) {
      console.log(e)
      setResultsList([])
      setHelpText('No results found.')
      setIsLoading(false)
    }
  }

  const handleDebounced = debounce(async (value: string) => {
    await fetchResults(value)
  }, 800)

  const handleTextChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (!value) {
      return resetInput()
    }
    handleDebounced(value)
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (!value) {
      resetInput()
    }
  }

  const handleDesktopSearchButtonClick = () => {
    const ele = keywordInputElement.current
    ele?.focus()
  }

  const renderGlobalSearchBody = () => {
    const emptyResult =
      !resultsList ||
      resultsList.every((item) => !item.results || item.results.length === 0)
    return (
      <div className="container-fluid">
        <div className="row">
          {isMobile && (
            <div className="mb-3">
              <input
                aria-label="Global Search"
                type="text"
                className="form-control"
                placeholder={placeholder}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleTextChange(e)
                }
                onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                  handleInputFocus(e)
                }
                ref={keywordInputElement}
              />
            </div>
          )}
        </div>
        {isLoading && (
          <div className="global-search-divider">
            <span />
          </div>
        )}
        {!isLoading && (
          <div className="row">
            {!emptyResult &&
              resultsList.map((group) =>
                ResultListGroup(
                  group.category,
                  group.results,
                  group.more_results_url,
                ),
              )}
            {emptyResult && (
              <span className="list-group-item text-start mb-2 global-search-help-text">
                {helpText}
              </span>
            )}
          </div>
        )}
      </div>
    )
  }

  if (!isMobile) {
    return (
      <>
        <div
          className="global-search-toggle app-search d-none d-lg-block w-100"
          data-bs-toggle="dropdown"
          onClick={handleDesktopSearchButtonClick}
        >
          <div className="input-group flex-nowrap">
            <span className="search-icon">
              <SearchIcon
                className="fa svg-inline--fa font-14"
                fill="currentColor"
              />
            </span>
            <input
              aria-label="Global Search"
              type="text"
              className="form-control global-search-button-desktop"
              placeholder={placeholder}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleTextChange(e)
              }
              onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                handleInputFocus(e)
              }
              ref={keywordInputElement}
            />
          </div>
        </div>
        <div className="dropdown-menu pt-3 pb-1 shadow global-search-dropdown">
          {renderGlobalSearchBody()}
        </div>
      </>
    )
  }
  return <div>{renderGlobalSearchBody()}</div>
}

const modalBody = document.querySelector('#global-search-modal-body')

if (modalBody) {
  const root = createRoot(modalBody)
  root.render(<GlobalSearch />)
}

// Desktop devices only
const mobileContainer = document.querySelector('.hyper-global-search-mobile')
if (
  mobileContainer &&
  getComputedStyle(mobileContainer as HTMLElement)?.display !== 'block'
) {
  const dropdownContainer = document.querySelector(
    '.hyper-global-search-desktop',
  )
  if (dropdownContainer) {
    const root = createRoot(dropdownContainer)
    root.render(<GlobalSearch />)
  }
}
